import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Workflow {
  id: string
  name: string
  description: string
  status: string
  trigger: { type: string; event?: string; schedule?: string }
  steps: { id: number; type: string; action: string }[]
  executions_total: number
  executions_success: number
  executions_failed: number
  last_execution: string | null
  created_by: string
}

interface Template {
  id: string
  name: string
  category: string
  steps_count: number
  usage_count: number
}

export default function WorkflowBuilderPage() {
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [wfRes, tplRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/workflow-builder/workflows'),
      fetch(API_BASE + '/workflow-builder/templates'),
      fetch(API_BASE + '/workflow-builder/statistics')
    ])
    if (wfRes.ok) setWorkflows((await wfRes.json()).workflows || [])
    if (tplRes.ok) setTemplates((await tplRes.json()).templates || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'paused': return 'bg-yellow-100 text-yellow-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTriggerIcon = (type: string) => {
    switch (type) {
      case 'event': return '⚡'
      case 'schedule': return '🕐'
      case 'condition': return '🎯'
      default: return '▶️'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Workflow Automation Builder</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Workflow</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Workflows</div>
              <div className="text-2xl font-bold">{statistics.total_workflows}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_workflows}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Executions</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_executions?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Success Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.success_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Templates</div>
              <div className="text-2xl font-bold">{statistics.templates_available}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Workflows</h2></div>
            <div className="divide-y">
              {workflows.map(wf => (
                <div key={wf.id} className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setSelectedWorkflow(wf)}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span>{getTriggerIcon(wf.trigger.type)}</span>
                        <span className="font-semibold">{wf.name}</span>
                      </div>
                      <div className="text-sm text-gray-500">{wf.description}</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(wf.status)}`}>{wf.status}</span>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500 mb-2">
                    <span>{wf.steps.length} steps</span>
                    <span className="text-green-600">{wf.executions_success} successful</span>
                    {wf.executions_failed > 0 && <span className="text-red-600">{wf.executions_failed} failed</span>}
                  </div>
                  {wf.last_execution && (
                    <div className="text-xs text-gray-400">Last run: {new Date(wf.last_execution).toLocaleString()}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {selectedWorkflow && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold">{selectedWorkflow.name}</h3>
                  <button className={`px-3 py-1 rounded text-sm ${selectedWorkflow.status === 'active' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                    {selectedWorkflow.status === 'active' ? 'Pause' : 'Activate'}
                  </button>
                </div>
                <div className="mb-4">
                  <div className="text-sm text-gray-500 mb-1">Trigger</div>
                  <div className="p-2 bg-blue-50 rounded">
                    <div className="font-medium">{selectedWorkflow.trigger.type}</div>
                    {selectedWorkflow.trigger.event && <div className="text-sm">{selectedWorkflow.trigger.event}</div>}
                    {selectedWorkflow.trigger.schedule && <div className="text-sm font-mono">{selectedWorkflow.trigger.schedule}</div>}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-sm text-gray-500 mb-2">Steps</div>
                  <div className="space-y-2">
                    {selectedWorkflow.steps.map((step, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{step.id}</div>
                        <div className="flex-1 p-2 bg-gray-50 rounded">
                          <span className="text-xs text-gray-500">{step.type}</span>
                          <div className="text-sm">{step.action}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Templates</h3>
              <div className="space-y-2">
                {templates.map(tpl => (
                  <div key={tpl.id} className="p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-sm">{tpl.name}</div>
                        <div className="text-xs text-gray-500">{tpl.category} • {tpl.steps_count} steps</div>
                      </div>
                      <span className="text-xs text-gray-400">{tpl.usage_count} uses</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
